// Lado de la página de la simulación: compila, arranca el Web Worker y reparte lo que llega
// (estado visual a la escena, Serial al monitor, avisos y sonido).
import { api } from './api.js';
import { Audio } from './audio.js';
import { getDef } from './parts/index.js';

const BOARD_CODE = { 'arduino-uno': 'uno', 'arduino-nano': 'nano', 'arduino-mega': 'mega', 'pi-pico': 'pico' };

export class SimController {
  constructor(app) {
    this.app = app;
    this.worker = null;
    this.state = 'stopped'; // stopped | compiling | running | paused | error
    this.speed = 1;
    this.audio = new Audio();
    this.lastFrame = null;
    this.projectTimer = null;
    this.compileSeq = 0;
  }

  get active() {
    return this.state === 'running' || this.state === 'paused';
  }

  setState(state, extra = {}) {
    this.state = state;
    this.app.emit('sim', { state, ...extra });
  }

  async compile() {
    const board = this.app.boardKey();
    if (!board) throw new Error('Agrega una placa al circuito para poder simular.');
    return api.post('/api/compile', { board, files: this.app.project.files, context: this.context() });
  }

  /**
   * De quién son las librerías propias que ve la compilación además de las tuyas (docs/PLANES.md, «Compilación»): el
   * proyecto guardado que está abierto, la tarea (trabajo del alumno o plantilla) o la entrega que se revisa.
   */
  context() {
    const { kind, projectId } = this.app;
    if (kind === 'project' || kind === 'foreign') return projectId ? { project: projectId } : undefined;
    const m = /^\/(?:tareas|entregas)\/([a-z0-9]+)/.exec(location.pathname);
    if (!m) return undefined;
    if (kind === 'work' || kind === 'template') return { assignment: m[1] };
    return kind === 'review' ? { submission: m[1] } : undefined;
  }

  async start() {
    if (this.state === 'compiling') return;
    this.audio.resume();
    if (this.active) this.stop();
    const seq = ++this.compileSeq;
    this.setState('compiling');
    let res;
    try {
      res = await this.compile();
    } catch (e) {
      if (seq !== this.compileSeq) return;
      // El tope de compilaciones (429, docs/PLANES.md) no es un error del sketch: se avisa aparte (client/ui/plan.js).
      if (e.status === 429) {
        this.setState('stopped');
        this.app.emit('compile-limit', e.message);
        return;
      }
      this.setState('error', { message: e.message });
      this.app.emit('compile', { ok: false, output: e.message, diagnostics: [] });
      return;
    }
    if (seq !== this.compileSeq) return;
    this.app.emit('compile', res);
    if (!res.ok) {
      this.setState('error', { message: 'Error de compilación' });
      return;
    }
    this.launch(res.hex, res.bin);
  }

  /** hex: programa de las AVR; bin: imagen de la flash (base64) de la Pico. */
  launch(hex, bin) {
    this.terminate();
    this.hex = hex;
    const worker = new Worker('/dist/worker.js', { type: 'module' });
    this.worker = worker;
    worker.onmessage = (e) => this.onMessage(e.data);
    worker.onerror = (e) => {
      // Sin mensaje = el script del simulador no cargó (p. ej. se actualizó la app mientras tanto).
      this.app.toast(e.message ? 'Error en el simulador: ' + e.message : 'No se pudo cargar el simulador. Recarga la página e inténtalo de nuevo.', true);
      this.stop();
    };
    this.boardAtStart = this.app.boardKey();
    if (this.eepromBoard !== this.boardAtStart) this.eeprom = null;
    this.eepromBoard = this.boardAtStart;
    this.filesAtStart = JSON.stringify(this.app.project.files);
    worker.postMessage({ type: 'start', hex, bin, project: this.app.simProject(), speed: this.speed, eeprom: this.eeprom });
    this.app.emit('serial-reset');
    this.setState('running');
  }

  onMessage(msg) {
    if (msg.type === 'frame') {
      for (const [id, v] of msg.visuals) {
        this.app.world.applyVisual(id, v);
        if (v && v.freq !== undefined) this.audio.set(id, v.freq, v.volume);
      }
      if (msg.serial) this.app.emit('serial', msg.serial);
      if (msg.eeprom) this.eeprom = msg.eeprom;
      this.lastFrame = msg;
      this.app.emit('frame', msg);
    } else if (msg.type === 'probe') {
      this.app.emit('probe', msg);
    } else if (msg.type === 'error') {
      console.error(msg.stack || msg.message);
      this.app.toast('El simulador se detuvo: ' + msg.message, true);
      this.stop();
    }
  }

  /** ¿El código cambió desde que se compiló lo que está corriendo? */
  get codeChanged() {
    return this.active && this.filesAtStart !== JSON.stringify(this.app.project.files);
  }

  /** Olvida la EEPROM simulada (al abrir otro proyecto). */
  forgetEeprom() {
    this.eeprom = null;
  }

  stop() {
    this.compileSeq++;
    this.terminate();
    this.audio.stopAll();
    this.app.world.resetVisuals();
    this.setState('stopped');
    this.app.emit('frame', null);
  }

  terminate() {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
  }

  pause(paused = this.state === 'running') {
    if (!this.worker) return;
    this.worker.postMessage({ type: 'pause', paused });
    if (paused) this.audio.stopAll();
    this.setState(paused ? 'paused' : 'running');
  }

  restart() {
    if (this.worker) this.worker.postMessage({ type: 'reset' });
  }

  setSpeed(speed) {
    this.speed = speed;
    if (this.worker) this.worker.postMessage({ type: 'speed', speed });
  }

  input(part, key, value) {
    if (this.worker) this.worker.postMessage({ type: 'input', part, key, value });
  }

  serial(text) {
    if (!this.worker) return;
    const bytes = [...new TextEncoder().encode(text)];
    this.worker.postMessage({ type: 'serial', bytes });
  }

  probe(pins) {
    if (this.worker) this.worker.postMessage({ type: 'probe', pins });
  }

  /** El circuito cambió con la simulación corriendo: se manda sin reiniciar el programa. */
  projectChanged() {
    clearTimeout(this.projectTimer);
    this.projectTimer = setTimeout(() => {
      if (!this.worker) return;
      const board = this.app.boardKey();
      if (!board || (this.boardAtStart && board !== this.boardAtStart)) {
        this.stop();
        return;
      }
      this.worker.postMessage({ type: 'project', project: this.app.simProject() });
    }, 60);
  }
}

export function boardCode(type) {
  return BOARD_CODE[type] || (getDef(type).mcu ? 'uno' : null);
}
